var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { css, html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ref } from "lit/directives/ref.js";
import "./HeapElementComponent";
import { defaults } from "../style";
import { ReferenceComponent } from "./ReferenceComponent";
import { deepAwaitMixin } from "../mixins/DeepAwaitMixin";
let HeapComponent = class HeapComponent extends deepAwaitMixin(LitElement) {
    constructor() {
        super(...arguments);
        this.leftMargins = new Map();
        this.leftMarginUpdates = new Map();
        this.heapConnections = new Map();
        this.handledByRecursion = new Map();
        this.endpointMap = {};
        this.referenceCount = new Map();
    }
    /**
     * Apply all margin updates to the heap elements
     * this recalculates the left margin for each element based on the connections between them
     */
    applyMarginUpdates() {
        this.heapConnections.forEach((values, key) => {
            values.forEach(value => {
                const source = this.endpointMap[key];
                const dest = this.endpointMap[value];
                if (source && dest) {
                    this.fixArrowDirection(source, value);
                }
            });
        });
        this.leftMargins.clear();
        for (const [key, value] of this.leftMarginUpdates.entries()) {
            this.leftMargins.set(key, value);
        }
        // this.handledByRecursion.clear();
    }
    /**
     * Reset all margin updates when a new frame is loaded
     */
    clearMargins() {
        this.leftMargins.clear();
        this.leftMarginUpdates.clear();
        this.heapConnections.clear();
        this.handledByRecursion.clear();
    }
    /**
     * Move heap elements to the right if they are to the left of the source element
     */
    fixArrowDirection(from, destId, prev = []) {
        const dest = this.endpointMap[destId];
        if (dest === undefined) {
            return;
        }
        // calculate the difference in position between the source and destination
        const sourceOffset = from.getBoundingClientRect().left + from.getBoundingClientRect().width;
        const destOffset = dest.getBoundingClientRect().left;
        const diff = sourceOffset - destOffset;
        // add current margin to the diff
        const destMargin = this.leftMargins.get(destId) || 0;
        let newMargin = destMargin + diff;
        // if the reference el was within a heap element, use that as the source
        // we need this because heap elements margin might have changed in this update cycle
        const source = from instanceof ReferenceComponent ? from.closest("tc-heap-element") : from;
        const sourceId = source ? parseInt(source.getAttribute("key") || "") : undefined;
        // // If the source location has already been updated, include that change in the diff
        if (sourceId && this.leftMarginUpdates.has(sourceId)) {
            const sourceMargin = this.leftMargins.get(sourceId) || 0;
            const sourceUpdate = this.leftMarginUpdates.get(sourceId) || 0;
            const sourceChange = sourceUpdate - sourceMargin;
            newMargin += sourceChange;
        }
        // if this element has been updated already, use the rightmost update
        const currentUpdate = this.leftMarginUpdates.get(destId) || 0;
        const newUpdate = Math.max(currentUpdate, newMargin);
        this.leftMarginUpdates.set(destId, Math.max(newUpdate, 0));
        // if the destination has any connections, update them too
        if (this.heapConnections.has(destId)) {
            // to prevent infinite loops, we keep track of which elements have been handled in previous calls
            const previous = [...prev, destId];
            this.heapConnections.get(destId)
                // only update elements that have not been handled by recursion
                .filter(id => !this.handledByRecursion.has(id) || !previous.some(x => this.handledByRecursion.get(id)?.has(x)))
                .forEach(id => {
                // keep track of which elements have been handled by recursion
                previous.forEach(r => {
                    if (!this.handledByRecursion.has(r)) {
                        this.handledByRecursion.set(r, new Set());
                    }
                    this.handledByRecursion.get(r).add(id);
                });
                this.fixArrowDirection(dest, id, previous);
            });
        }
        // margin updates require a re-render
        this.requestUpdate();
    }
    addEndpoint(id, ref) {
        // Avoid writing to the map if the reference is undefined
        // This can happen if two calls happen in quick succession
        if (ref === undefined) {
            return;
        }
        this.endpointMap[id] = ref;
    }
    get updateComplete() {
        return Promise.all(Object.values(this.endpointMap)
            .map(el => el.updateComplete))
            .then(() => super.updateComplete);
    }
    addConnection(from, to, ref) {
        const existing = this.heapConnections.get(from) || [];
        this.heapConnections.set(from, [...existing, to]);
        const removeConnection = (e) => {
            this.removeConnection(from, e.detail.to);
            e.detail.from.removeEventListener("remove-reference", removeConnection);
        };
        ref.addEventListener("remove-reference", removeConnection);
    }
    removeConnection(from, to) {
        const existing = this.heapConnections.get(from) || [];
        this.heapConnections.set(from, existing.filter(x => x !== to));
    }
    addReference(id) {
        const count = this.referenceCount.get(id) || 0;
        this.referenceCount.set(id, count + 1);
        if (count === 0) {
            this.scheduleRedraw();
        }
    }
    removeReference(id) {
        const count = this.referenceCount.get(id) || 0;
        this.referenceCount.set(id, Math.max(0, count - 1));
        if (count === 1) {
            this.scheduleRedraw();
        }
    }
    /**
     * Schedule a redraw of the heap elements
     * This is necessary because the margin updates require a re-render
     */
    async scheduleRedraw() {
        await this.deepUpdateComplete;
        this.requestUpdate();
    }
    render() {
        this.applyMarginUpdates();
        return html `
            ${this.layout.layout.map(node => html `
                <div class="row">
                    ${node.toArray().map(key => html `
                        <tc-heap-element .el=${this.layout.heap[key]}
                                         class=${!this.referenceCount.get(key) ? "hidden" : ""}
                                         style=${`margin-left: ${this.leftMargins.get(key) || 0}px`}
                                         key="${key}"
                                         ${ref(r => this.addEndpoint(key, r))}
                                         @add-reference="${(e) => this.addConnection(key, e.detail.to, e.detail.from)}"
                                         @deep-update="${() => this.scheduleRedraw()}"
                        </tc-heap-element>
                    `)}
                </div>
            `)}
        `;
    }
};
HeapComponent.styles = [defaults, css `
        .row {
            display: flex;
            flex-direction: row;
            min-height: 0;
        }
        
        tc-heap-element {
            margin: 0.5rem 1rem;
        }
        
        tc-heap-element.hidden {
            opacity: 0;
            width: 0;
            height: 0;
            margin: 0;
        }
    `];
__decorate([
    property({ type: Object })
], HeapComponent.prototype, "layout", void 0);
HeapComponent = __decorate([
    customElement('tc-heap')
], HeapComponent);
export { HeapComponent };
//# sourceMappingURL=HeapComponent.js.map